import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaBuilding } from 'react-icons/fa';
import AssetuserSidebar from './AssetuserSidebar';
import * as assetUserService from '../services/assetUserService';
import '../styles/AssetRequestForm.css';

const DURATION_OPTIONS = [
  { value: '1_week', label: '1 Week' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '3_weeks', label: '3 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: '4_months', label: '4 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '1_year', label: '1 Year' }
];

const AssetRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const assetIdFromState = location.state?.assetId;

  const [formData, setFormData] = useState({
    assetId: assetIdFromState || '',
    deskLocation: '',
    reason: '',
    requestType: 'new_request',
    swapWithAssetId: '',
    duration: '',
    startDate: '',
    breakdownDescription: '',
    extendDuration: '',
    requestDepartment: '',
    requestedQuantity: 1,
    unitId: ''
  });

  const [availableAssets, setAvailableAssets] = useState([]);
  const [myAssets, setMyAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assetStock, setAssetStock] = useState({});
  const [allocatedUnits, setAllocatedUnits] = useState([]); // Track allocated units

  useEffect(() => {
    if (user.role !== 'asset_user') {
      navigate('/');
      return;
    }
    fetchData();
  }, [navigate, user.role]);

  const getSelectedAsset = () => {
    if (!formData.assetId) return null;
    
    try {
      // For new_request and swap_request, search in availableAssets
      if (formData.requestType === 'new_request' || formData.requestType === 'swap_request') {
        if (!Array.isArray(availableAssets)) return null;
        const asset = availableAssets.find(a => a && a._id && a._id === formData.assetId);
        return asset && asset.assetName ? asset : null;
      }
      
      // For breakdown, release, extension - search in myAssets (which are assignments with nested asset)
      if (!Array.isArray(myAssets)) return null;
      
      const assignment = myAssets.find(a => {
        if (!a || !a.assetId) return false;
        // Check if assetId is an object with _id or just an ID string
        const assetId = typeof a.assetId === 'object' ? a.assetId._id : a.assetId;
        return assetId === formData.assetId;
      });
      
      if (assignment && assignment.assetId && typeof assignment.assetId === 'object' && assignment.assetId.assetName) {
        return assignment.assetId;
      }
      return null;
    } catch (err) {
      console.error('Error in getSelectedAsset:', err);
      return null;
    }
  };

  const selectedAsset = getSelectedAsset();

  useEffect(() => {
    // Fetch stock information when asset is selected
    if (formData.assetId && (formData.requestType === 'new_request' || formData.requestType === 'swap_request')) {
      fetchAssetStock(formData.assetId);
    }
    // Reset allocated units when asset changes
    setAllocatedUnits([]);
  }, [formData.assetId, formData.requestType]);

  // Auto-allocate units when selectedAsset is loaded or quantity changes
  useEffect(() => {
    if (selectedAsset && selectedAsset.units && (formData.requestType === 'new_request' || formData.requestType === 'swap_request')) {
      const quantity = parseInt(formData.requestedQuantity) || 1;
      const availableUnits = selectedAsset.units
        .filter(u => u.status === 'available')
        .sort((a, b) => a.unitId.localeCompare(b.unitId))
        .slice(0, quantity)
        .map(u => u.unitId);
      
      setAllocatedUnits(availableUnits);
    }
  }, [selectedAsset, formData.requestedQuantity, formData.requestType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [availableData, myData, locData] = await Promise.all([
        assetUserService.getAvailableAssets(),
        assetUserService.getMyAssets(),
        assetUserService.getLocations()
      ]);
      setAvailableAssets(availableData.assets || []);
      setMyAssets(myData.assets || []);
      setLocations(locData.locations || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetStock = async (assetId) => {
    try {
      const response = await assetUserService.getAssetStock(assetId);
      setAssetStock(prev => ({
        ...prev,
        [assetId]: response.stock || {}
      }));
    } catch (err) {
      console.error('Error fetching asset stock:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-allocate units when quantity changes
    if (name === 'requestedQuantity' && selectedAsset && selectedAsset.units) {
      const quantity = parseInt(value) || 1;
      const availableUnits = selectedAsset.units
        .filter(u => u.status === 'available')
        .sort((a, b) => a.unitId.localeCompare(b.unitId))
        .slice(0, quantity)
        .map(u => u.unitId);
      
      setAllocatedUnits(availableUnits);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate based on request type
    if (formData.requestType === 'new_request' || formData.requestType === 'swap_request') {
      if (!formData.assetId || !formData.duration || !formData.startDate || !formData.requestDepartment) {
        setError('Please fill in all required fields (Asset, Start Date, Duration, and Department)');
        return;
      }
      if (formData.requestType === 'swap_request' && !formData.swapWithAssetId) {
        setError('Please select the asset you want to swap out');
        return;
      }
      // Validate quantity doesn't exceed available
      const requestedQty = parseInt(formData.requestedQuantity) || 1;
      const availableQty = assetStock[formData.assetId]?.availableQuantity || 0;
      if (requestedQty > availableQty) {
        setError(`Cannot request ${requestedQty} units. Only ${availableQty} unit(s) available.`);
        return;
      }
      
      // Validate that enough units are available for allocation
      if (allocatedUnits.length < requestedQty) {
        setError(`Only ${allocatedUnits.length} unit(s) available but ${requestedQty} requested. Please select a lower quantity.`);
        return;
      }
    } else if (formData.requestType === 'asset_breakdown') {
      if (!formData.assetId || !formData.breakdownDescription) {
        setError('Please select an asset and describe the breakdown issue');
        return;
      }
    } else if (formData.requestType === 'asset_release') {
      if (!formData.assetId) {
        setError('Please select the asset to release');
        return;
      }
    } else if (formData.requestType === 'asset_extension') {
      if (!formData.assetId || !formData.extendDuration) {
        setError('Please select an asset and extension duration');
        return;
      }
    }

    setLoading(true);
    try {
      // Include allocated units in the submission for reference
      const submitData = {
        ...formData,
        allocatedUnits: allocatedUnits.length > 0 ? allocatedUnits : undefined
      };
      
      await assetUserService.createAssetRequest(submitData);
      setSuccess(`${getRequestTypeLabel()} created successfully!`);
      setTimeout(() => {
        navigate('/asset-user-dashboard');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to create request');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRequestTypeLabel = () => {
    const labels = {
      'new_request': 'Asset request',
      'swap_request': 'Swap request',
      'asset_breakdown': 'Breakdown request',
      'asset_release': 'Release request',
      'asset_extension': 'Extension request'
    };
    return labels[formData.requestType];
  };

  return (
    <div className="asset-request-page">
      <AssetuserSidebar user={user} activeTab="asset-request" onTabChange={(tab) => {
        if (tab !== 'asset-request') {
          navigate('/asset-user-dashboard');
        }
      }} />
      <div className="page-container">
        <div className="page-header-request">
          <button className="btn-back" onClick={() => navigate('/asset-user-dashboard')}>
            <FaArrowLeft /> Back
          </button>
          <h1>Request Asset</h1>
        </div>

        <div className="request-container">
        {/* Requester Info */}

        <form onSubmit={handleSubmit} className="request-form">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-section">
            <h2>Request Type</h2>
            <div className="form-group">
              <label>Request Type *</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="requestType"
                    value="new_request"
                    checked={formData.requestType === 'new_request'}
                    onChange={handleChange}
                  />
                  <span>Request New Asset</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="requestType"
                    value="swap_request"
                    checked={formData.requestType === 'swap_request'}
                    onChange={handleChange}
                  />
                  <span>Swap Assets</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="requestType"
                    value="asset_breakdown"
                    checked={formData.requestType === 'asset_breakdown'}
                    onChange={handleChange}
                  />
                  <span>Report my Asset Breakdown</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="requestType"
                    value="asset_release"
                    checked={formData.requestType === 'asset_release'}
                    onChange={handleChange}
                  />
                  <span>Release my Asset</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    name="requestType"
                    value="asset_extension"
                    checked={formData.requestType === 'asset_extension'}
                    onChange={handleChange}
                  />
                  <span>Extend my Asset Usage</span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Asset Details</h2>

            <div className="form-group">
              <label>
                {formData.requestType === 'new_request' && 'Select Asset to Request *'}
                {formData.requestType === 'swap_request' && 'Select Asset to Request *'}
                {formData.requestType === 'asset_breakdown' && 'Select Asset with Issue *'}
                {formData.requestType === 'asset_release' && 'Select Asset to Release *'}
                {formData.requestType === 'asset_extension' && 'Select Asset to Extend *'}
              </label>
              <select
                name="assetId"
                value={formData.assetId}
                onChange={handleChange}
                required
                className="select-input"
              >
                <option value="">-- Choose an asset --</option>
                {(formData.requestType === 'new_request' || formData.requestType === 'swap_request') && 
                  availableAssets && availableAssets.length > 0 && 
                  availableAssets.filter(a => a && a.assetName).map(asset => (
                    <option key={asset._id} value={asset._id}>
                      {asset.assetName} ({asset.serialNumber || 'N/A'})
                    </option>
                  ))
                }
                {(formData.requestType === 'asset_breakdown' || formData.requestType === 'asset_release' || formData.requestType === 'asset_extension') && 
                  myAssets && myAssets.length > 0 && 
                  myAssets.filter(a => {
                    if (!a || !a.assetId) return false;
                    const assetObj = typeof a.assetId === 'object' ? a.assetId : null;
                    return assetObj && assetObj.assetName;
                  }).map(assignment => {
                    const asset = assignment.assetId;
                    return (
                      <option key={assignment._id} value={asset._id}>
                        {asset.assetName} ({asset.serialNumber || 'N/A'})
                      </option>
                    );
                  })
                }
              </select>
            </div>

            {(formData.requestType === 'new_request' || formData.requestType === 'swap_request') && selectedAsset && (
              <div className="form-group stock-info">
                <label>Stock Availability</label>
                {assetStock[formData.assetId] ? (
                  <div className="stock-display">
                    <div className="stock-item">
                      <span className="stock-label">Total Units:</span>
                      <span className="stock-value">{assetStock[formData.assetId].totalQuantity || 0}</span>
                    </div>
                    <div className="stock-item">
                      <span className="stock-label">Available:</span>
                      <span className="stock-value available">{assetStock[formData.assetId].availableQuantity || 0}</span>
                    </div>
                    <div className="stock-item">
                      <span className="stock-label">Assigned:</span>
                      <span className="stock-value assigned">{assetStock[formData.assetId].assignedQuantity || 0}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#999', fontSize: '0.9em' }}>Loading stock information...</div>
                )}
              </div>
            )}

            {(formData.requestType === 'new_request' || formData.requestType === 'swap_request') && (
              <div className="form-group">
                <label>Quantity to Request *</label>
                <select
                  name="requestedQuantity"
                  value={formData.requestedQuantity}
                  onChange={handleChange}
                  required
                  className="select-input"
                >
                  {[1, 2, 3, 4, 5].map(num => (
                    <option key={num} value={num}>{num} Unit(s)</option>
                  ))}
                </select>
              </div>
            )}

            {(formData.requestType === 'new_request' || formData.requestType === 'swap_request') && selectedAsset && allocatedUnits.length > 0 && (
              <div className="form-group allocated-units-display">
                <label>✓ Units to be Allocated</label>
                <div className="allocated-units-box">
                  <strong>System will automatically assign:</strong>
                  <div className="units-list">
                    {allocatedUnits.map((unitId, idx) => (
                      <span key={idx} className="unit-badge">
                        <strong>{unitId}</strong>
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '0.85em', color: '#666' }}>
                    {allocatedUnits.length} unit(s) selected - These are the specific units from your asset inventory that you'll receive.
                  </div>
                </div>
              </div>
            )}

            {(formData.requestType === 'new_request' || formData.requestType === 'swap_request') && selectedAsset && selectedAsset.units && selectedAsset.units.length > 0 && (
              <div className="form-group">
                <label>Select Specific Unit (Optional)</label>
                <select
                  name="unitId"
                  value={formData.unitId}
                  onChange={handleChange}
                  className="select-input"
                >
                  <option value="">-- Any available unit --</option>
                  {selectedAsset.units.filter(u => u.status === 'available').map(unit => (
                    <option key={unit.unitId} value={unit.unitId}>
                      {unit.unitId} ({unit.status})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(formData.requestType === 'new_request' || formData.requestType === 'swap_request') && (
              <div className="form-group">
                <label>Department for Use *</label>
                <select
                  name="requestDepartment"
                  value={formData.requestDepartment}
                  onChange={handleChange}
                  required={formData.requestType === 'new_request' || formData.requestType === 'swap_request'}
                  className="select-input"
                >
                  <option value="">-- Select the department where you'll use this asset --</option>
                  {locations && locations.length > 0 && 
                    locations.map(dept => (
                      <option key={dept._id || dept.name} value={dept.name || dept._id}>
                        {dept.label || dept.name}
                      </option>
                    ))
                  }
                </select>
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '0.9em', color: '#1565c0' }}>
                  <strong>ℹ️ Note:</strong> The asset incharge of the selected department will receive your request.
                </div>
              </div>
            )}
          
            {formData.requestType === 'asset_breakdown' && (
              <div className="form-group">
                <label>Describe the Issue / Breakdown *</label>
                <textarea
                  name="breakdownDescription"
                  placeholder="Describe what's wrong with the asset, symptoms, when it started, etc."
                  value={formData.breakdownDescription}
                  onChange={handleChange}
                  rows="4"
                  className="textarea-input"
                  required
                />
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '0.9em' }}>
                  <strong>ℹ️ Note:</strong> Your asset incharge will receive this request and can create a maintenance issue with a technician.
                </div>
              </div>
            )}

            {formData.requestType === 'asset_extension' && (
              <div className="form-group">
                <label>Extend Duration By *</label>
                <select
                  name="extendDuration"
                  value={formData.extendDuration}
                  onChange={handleChange}
                  required={formData.requestType === 'asset_extension'}
                  className="select-input"
                >
                  <option value="">-- Select extension period --</option>
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '0.9em', color: '#1565c0' }}>
                  <strong>ℹ️ Note:</strong> The extension duration will be added to your current asset usage period.
                </div>
              </div>
            )}

            {formData.requestType === 'asset_release' && (
              <div className="form-group">
                <label>Reason for Release</label>
                <textarea
                  name="reason"
                  placeholder="Why do you want to release this asset? (optional)"
                  value={formData.reason}
                  onChange={handleChange}
                  rows="3"
                  className="textarea-input"
                />
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f3e5f5', borderRadius: '4px', fontSize: '0.9em' }}>
                  <strong>ℹ️ Note:</strong> Once approved, this asset will be returned to your department.
                </div>
              </div>
            )}
            {selectedAsset && (
              <div className="asset-preview">
                <h4>Asset Details</h4>
                <div className="preview-info">
                  <div><strong>Name:</strong> {selectedAsset?.assetName || 'N/A'}</div>
                  <div><strong>Serial:</strong> {selectedAsset?.serialNumber || '—'}</div>
                  <div><strong>Category:</strong> {selectedAsset?.category || 'N/A'}</div>
                  <div><strong>Brand:</strong> {selectedAsset?.brand || '—'}</div>
                  <div><strong>Model:</strong> {selectedAsset?.model || '—'}</div>
                  <div><strong>Current Location:</strong> {selectedAsset?.currentLocation || selectedAsset?.department || 'N/A'}</div>
                  <div><strong>Condition:</strong> {selectedAsset?.condition || 'N/A'}</div>
                  <div><strong><FaBuilding /> Managed By Department:</strong> {selectedAsset?.currentDepartment || selectedAsset?.department || 'Unassigned'}</div>
                  
                  {/* Show unit information if available */}
                  {selectedAsset?.units && selectedAsset.units.length > 0 && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                      <strong style={{ display: 'block', marginBottom: '8px' }}>Unit Details:</strong>
                      <div style={{ marginLeft: '10px', fontSize: '0.9em' }}>
                        <div><strong>Total Units:</strong> {selectedAsset.units.length}</div>
                        <div><strong>Available Units:</strong> {selectedAsset.units.filter(u => u.status === 'available').length}</div>
                        <div style={{ marginTop: '8px' }}>
                          <strong>Unit IDs:</strong>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                            {selectedAsset.units.map(unit => (
                              <span key={unit.unitId} style={{
                                padding: '5px 10px',
                                backgroundColor: unit.status === 'available' ? '#c8e6c9' : '#ffccbc',
                                borderRadius: '4px',
                                fontSize: '0.85em',
                                fontWeight: 'bold',
                                color: unit.status === 'available' ? '#2e7d32' : '#d84315'
                              }}>
                                {unit.unitId} ({unit.status})
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {(selectedAsset?.currentDepartment || selectedAsset?.department) && (
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '0.9em', color: '#1565c0' }}>
                      <strong>📋 Request will be routed to the {selectedAsset?.currentDepartment || selectedAsset?.department} department's asset incharge for approval.</strong>
                    </div>
                  )}
                </div>
              </div>
            )}

            {formData.requestType === 'swap_request' && myAssets.length > 0 && (
              <div className="form-group">
                <label>Asset to Swap Out (from your current assets) *</label>
                <select
                  name="swapWithAssetId"
                  value={formData.swapWithAssetId}
                  onChange={handleChange}
                  required={formData.requestType === 'swap_request'}
                  className="select-input"
                >
                  <option value="">-- Choose asset to return --</option>
                  {myAssets && myAssets.length > 0 && myAssets.filter(a => {
                    if (!a || !a.assetId) return false;
                    const assetObj = typeof a.assetId === 'object' ? a.assetId : null;
                    return assetObj && assetObj.assetName;
                  }).map(assignment => {
                    const asset = assignment.assetId;
                    return (
                      <option key={assignment._id} value={asset._id}>
                        {asset.assetName} ({asset.serialNumber || 'N/A'})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          {(formData.requestType === 'new_request' || formData.requestType === 'swap_request') && (
          <div className="form-section">
            <h2>Duration & Location</h2>

            <div className="form-group">
              <label>From Date (When do you need the asset?) *</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                required
                className="text-input"
              />
            </div>

            <div className="form-group">
              <label>Duration (How long do you need this asset?) *</label>
              <select
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                className="select-input"
              >
                <option value="">-- Select duration --</option>
                {DURATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>



            <div className="form-group">
              <label>Desk / Station Location</label>
              <input
                type="text"
                name="deskLocation"
                placeholder="e.g., Desk A-5, Station 2, Bay 3"
                value={formData.deskLocation}
                onChange={handleChange}
                className="text-input"
              />
            </div>

            <div className="form-group">
              <label>Reason for Request</label>
              <textarea
                name="reason"
                placeholder="Why do you need this asset? What will you use it for?"
                value={formData.reason}
                onChange={handleChange}
                rows="4"
                className="textarea-input"
              />
            </div>
          </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate('/asset-user-dashboard')}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>

        {formData.requestType === 'swap_request' && myAssets.length === 0 && (
          <div className="info-box">
            <strong>Note:</strong> You don't have any assigned assets to swap. Please select "New Asset Request" instead.
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AssetRequestForm;
